import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import PublicNavigation from '../../components/PublicNavigation';
import './ChapterKPIDashboard.css';

function ChapterKPIDashboard() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    period: 'yearly'
  });
  const navigate = useNavigate();

  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    try {
      const snap = await getDocs(collection(db, 'manualProjects'));
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setProjects(data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }

  const extractSchools = (beneficiaryName) => {
    if (!beneficiaryName) return [];
    return beneficiaryName.split(/[\n,]+/).map(n => n.trim()).filter(n => n.length > 0);
  };

  const getFunds = (project) => {
    if (project.totalFunds) {
      return {
        received: project.totalFunds.received || 0,
        disbursed: project.totalFunds.disbursed || 0,
        openingBalance: project.totalFunds.openingBalance || 0,
        closingBalance: project.totalFunds.closingBalance || 0
      };
    }
    return {
      received: project.fundReceived || 0,
      disbursed: project.fundDisbursed || 0,
      openingBalance: project.fundOpeningBalance || 0,
      closingBalance: project.fundClosingBalance || 0
    };
  };

  const formatINR = (amount) => {
    return `₹${Math.round(amount).toLocaleString('en-IN')}`;
  };

  const getChapterSummary = () => {
    const filtered = projects.filter(p => p.reportYear === filters.year);
    const byChapter = {};

    filtered.forEach(p => {
      const ch = p.chapter || 'No Chapter';
      if (!byChapter[ch]) {
        byChapter[ch] = {
          totalProjects: 0,
          totalSchools: 0,
          totalBeneficiaries: 0,
          activeProjects: 0,
          completedProjects: 0,
          totalFundsReceived: 0,
          totalFundsDisbursed: 0,
          districts: new Set(),
          categories: {},
          beneficiaryCategories: {},
          schools: new Set()
        };
      }

      byChapter[ch].totalProjects++;
      byChapter[ch].totalBeneficiaries += p.totalBeneficiaries || 0;
      
      const funds = getFunds(p);
      byChapter[ch].totalFundsReceived += funds.received;
      byChapter[ch].totalFundsDisbursed += funds.disbursed;
      
      if (p.status === 'Active') byChapter[ch].activeProjects++;
      if (p.status === 'Completed') byChapter[ch].completedProjects++;
      
      if (p.district) byChapter[ch].districts.add(p.district);
      
      extractSchools(p.beneficiaryName).forEach(school => {
        byChapter[ch].schools.add(school);
      });

      if (p.beneficiaryCategories) {
        Object.entries(p.beneficiaryCategories).forEach(([cat, count]) => {
          if (!byChapter[ch].beneficiaryCategories[cat]) {
            byChapter[ch].beneficiaryCategories[cat] = 0;
          }
          byChapter[ch].beneficiaryCategories[cat] += parseInt(count) || 0;
        });
      }
    });

    return Object.entries(byChapter).map(([name, data]) => {
      const sortedCategories = Object.entries(data.beneficiaryCategories)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .reduce((obj, [key, val]) => ({ ...obj, [key]: val }), {});
      
      return {
        name,
        ...data,
        beneficiaryCategories: sortedCategories,
        totalSchools: data.schools.size,
        districtCount: data.districts.size,
        fundsAvailable: data.totalFundsReceived - data.totalFundsDisbursed
      };
    }).sort((a, b) => b.totalProjects - a.totalProjects);
  };

  const chapterSummary = getChapterSummary();

  if (loading) return (
    <div className="kpi-page">
      <PublicNavigation />
      <div className="loading">Loading...</div>
    </div>
  );

  return (
    <div className="kpi-page">
      <PublicNavigation />
      
      <div className="kpi-container">
        <div className="kpi-header">
          <div className="header-content">
            <h1>📊 Chapter Impact Dashboard</h1>
            <p className="subtitle">TNF USA - Chapter Performance Analytics</p>
          </div>
          <div className="year-filter">
            <label>Year:</label>
            <select value={filters.year} onChange={(e) => setFilters({...filters, year: parseInt(e.target.value)})}>
              {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        <div className="summary-bar">
          <div className="summary-item">
            <span className="label">Total Chapters</span>
            <span className="value">{chapterSummary.length}</span>
          </div>
          <div className="summary-item">
            <span className="label">Total Projects</span>
            <span className="value">{chapterSummary.reduce((sum, c) => sum + c.totalProjects, 0)}</span>
          </div>
          <div className="summary-item">
            <span className="label">Total Schools</span>
            <span className="value">{chapterSummary.reduce((sum, c) => sum + c.totalSchools, 0)}</span>
          </div>
          <div className="summary-item">
            <span className="label">Total Beneficiaries</span>
            <span className="value">{chapterSummary.reduce((sum, c) => sum + c.totalBeneficiaries, 0).toLocaleString('en-IN')}</span>
          </div>
        </div>

        <div className="chapters-grid">
          {chapterSummary.map(chapter => (
            <div key={chapter.name} className="chapter-card" onClick={() => navigate(`/dashboards/chapter/${encodeURIComponent(chapter.name)}`)}>
              <div className="card-header">
                <h2>{chapter.name}</h2>
                <span className="badge">{chapter.districtCount} Districts</span>
              </div>

              <div className="metrics-row">
                <div className="metric">
                  <div className="metric-value">{chapter.totalProjects}</div>
                  <div className="metric-label">Projects</div>
                </div>
                <div className="metric">
                  <div className="metric-value">{chapter.totalSchools}</div>
                  <div className="metric-label">Schools</div>
                </div>
                <div className="metric">
                  <div className="metric-value">{chapter.totalBeneficiaries.toLocaleString('en-IN')}</div>
                  <div className="metric-label">Beneficiaries</div>
                </div>
              </div>

              <div className="status-row">
                <div className="status-item active">
                  <span className="dot"></span>
                  <span>{chapter.activeProjects} Active</span>
                </div>
                <div className="status-item completed">
                  <span className="dot"></span>
                  <span>{chapter.completedProjects} Completed</span>
                </div>
              </div>

              <div className="funds-row">
                <div className="fund-item">
                  <span className="label">Received:</span>
                  <span className="amount">{formatINR(chapter.totalFundsReceived)}</span>
                </div>
                <div className="fund-item">
                  <span className="label">Disbursed:</span>
                  <span className="amount">{formatINR(chapter.totalFundsDisbursed)}</span>
                </div>
              </div>

              <div className="categories-preview">
                {Object.entries(chapter.beneficiaryCategories).slice(0, 3).map(([cat, count]) => (
                  <div key={cat} className="category-tag">
                    <span className="cat-name">{cat}</span>
                    <span className="cat-count">{count}</span>
                  </div>
                ))}
              </div>

              <div className="view-details">
                Click to view detailed KPIs →
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ChapterKPIDashboard;
